import type { IconType } from "@/components/ui/Icon";
import { Icon } from "@/components/ui/Icon";
import type { Route } from "@deskpro-product/messenger-api-schemas/messenger/config/routes";
import { clsx } from "clsx";
import Badge from "@/components/ui/Badge/Badge";
import { useConversationsContext } from "../../lib/client/contexts/ConversationsContext";
import { Link, shallow, useRouterState } from "@tanstack/react-router";
import useIsMessageHistoryEnabled from "../../lib/client/hooks/useIsMessageHistoryEnabled";
import useCurrentConversationId from "../../lib/client/hooks/useCurrentConversationId";
import { useCallback, useMemo } from "react";

export default function NavItem({
  params,
  route,
  title,
}: {
  params: { tenantId: string; messengerId: string; hostConfig: string; locale: string };
  route: Route;
  title: string;
}) {
  const currentConversationId = useCurrentConversationId();
  const isMessageHistoryEnabled = useIsMessageHistoryEnabled();
  const { unreadAll, unreadForCurrent } = useConversationsContext(
    useCallback(
      (c) => ({
        unreadAll: c.getUnreadCountExcept(),
        unreadForCurrent: currentConversationId
          ? c.getUnreadCount(currentConversationId)
          : c.getUnreadCount(),
      }),
      [currentConversationId],
    ),
    shallow,
  );
  const isMessagesRoute = route.type === "MessagesRoute";

  const unreadCount = useMemo(
    () => isMessagesRoute && (isMessageHistoryEnabled ? unreadAll : unreadForCurrent),
    [isMessageHistoryEnabled, isMessagesRoute, unreadAll, unreadForCurrent],
  );

  const isActive = useActiveRouteSegment(route.type);
  return (
    <Link
      to={`/$tenantId/$messengerId/$hostConfig/$locale/${routeMap[route.type].path}`}
      params={params}
      className={clsx(
        "align-center group flex w-full flex-1 justify-center no-underline transition-colors duration-150",
        isActive
          ? "text-[--color500] dark:text-[--color300]"
          : "text-gray-600 hover:text-[--color500] dark:text-gray-400 dark:hover:text-[--color300]",
        isActive && "cursor-default",
      )}
    >
      <div
        className={clsx(
          "flex flex-col items-center transition-transform duration-150",
          !isActive && "group-hover:scale-110",
        )}
      >
        <span className="relative leading-none">
          {unreadCount ? <Badge count={unreadCount} /> : null}
          <Icon
            icon={isActive ? routeMap[route.type].iconActive : routeMap[route.type].icon}
            className="text-2xl"
          />
        </span>
        <span className="text-xs font-semibold ">{title}</span>
      </div>
    </Link>
  );
}

const useActiveRouteSegment = (routeType: Route["type"]): boolean => {
  const router = useRouterState();
  const segments = router.location.pathname.split("/");
  const activeSegment = segments.length > 5 ? segments[6] : undefined;
  return routeMap[routeType].path === activeSegment;
};

const routeMap: Record<
  Route["type"],
  {
    icon: IconType;
    iconActive: IconType;
    path: "home" | "help" | "messages" | "news";
  }
> = {
  HomeRoute: {
    icon: "dazzle-linear-house-floor",
    iconActive: "dazzle-solid-house-floor",
    path: "home",
  },
  HelpcenterRoute: {
    icon: "dazzle-linear-search-alt",
    iconActive: "dazzle-solid-search-alt",
    path: "help",
  },
  MessagesRoute: {
    icon: "dazzle-linear-message-circle-chat",
    iconActive: "dazzle-solid-message-circle-chat",
    path: "messages",
  },
  NewsRoute: {
    icon: "heroicons-linear-newspaper",
    iconActive: "heroicons-solid-newspaper",
    path: "news",
  },
};
